import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {WidgetData} from "../../../../../core/shared/widget/widget-data";
import {WidgetDataParam} from "../../../../../core/shared/widget/widget-data-param";
import {MvsObjectNavigationService} from "../../../../../core/shared/navigation/mvs-object-navigation.service";
import {MvsObjectNavigationEntry} from "../../../../../core/shared/navigation/mvs-object-navigation-entry";
import {MvsObjectNavigationActionEnum} from "../../../../../core/shared/navigation/mvs-object-navigation-action-enum";
import {WidgetFactory} from "../../../../../core/shared/widget/service/widget.factory";
import {WidgetConfigInterface} from "../../../../../core/shared/mvs-page/interface/widget-config-page.interface";
import {ConfigPageComponent} from "../../../../../core/shared/mvs-page/config-page.component";
import {NavigationItem} from "../../../../../core/shared/dto/navigation/navigation-item";
import {PageHeaderConfig} from "../../../../../core/shared/service/page/page-header-config.interface";
import {
    MvsConfigHeaderComponent
} from "../../../../../core/shared/components/mvs-config-header/mvs-config-header.component";
import {FilterCriteria} from "../../../../../core/shared/filter/api/filter.criteria";
import {ObjectIdentifier} from "../../../../../core/shared/basic/object-identifier";
import {ObjectIdentifierData} from "../../../../../core/shared/basic/object-identifier-data";
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from "../../../../../core/shared/helper/widget-function-call-back-create";

@Component({
    selector: 'mvs-xx-config-page',
    templateUrl: './xx-config.page.html',
    styleUrls: ['./xx-config.page.scss'],
    standalone: false
})
export class XxConfigPage extends ConfigPageComponent implements OnInit, OnDestroy, OnChanges {

    defaultLabel: string = 'XX Konfiguration';

    navigationItems: NavigationItem[] = [
        {
            label: 'Einfacher Test',
            action: 'xx.XxSimpleTestEntity',
            tooltip: 'Einfache Testeintraege',
            icon: 'fa-solid fa-flask',
            toggleable: false,
            default: true,
        },
        {
            label: 'Car-Pilot Fahrzeugtypen',
            action: 'xx.XxCarPilotCarType',
            tooltip: 'Car-Pilot Fahrzeugtypen und zugeordnete Felder',
            icon: 'fa-solid fa-car',
            toggleable: false,
        },
        {
            label: 'Generische Testtypen',
            action: 'xx.XxGenericTestType',
            tooltip: 'Generische Testtypen und zugeordnete Felder',
            icon: 'fa-solid fa-vial',
            toggleable: false,
        },
    ];

    override widgets: { [key: string]: WidgetConfigInterface } = {
        simpleTestWidget: {
            alias: 'xx.config.simple.test.widget.new',
            label: 'Einfacher Test',
            entity: 'xx.XxSimpleTestEntity',
            widgetData: new WidgetData(),
            createFunction: (widget: WidgetConfigInterface) => {
                widget.widgetData = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
                    widget.alias, widget.label, widget.entity
                );
            }
        },
        xxCarPilotCarTypeSelectableWidget: {
            alias: 'xx.car.pilot.car.type.widget.selectable.new',
            label: 'Car-Pilot Fahrzeugtypen',
            entity: 'xx.XxCarPilotCarType',
            widgetData: new WidgetData(),
            postFunction: (widget: WidgetConfigInterface) => {
                widget.widgetData.uiComponent = 'selectable';
                widget.widgetData.setParam(WidgetDataParam.create('attributeToDisplay', 'e.name'));
                widget.widgetData.setParam(WidgetDataParam.create('showSearch', true));
            }
        },
        xxGenericTestTypeSelectableWidget: {
            alias: 'xx.generic.test.type.widget.selectable.new',
            label: 'Generische Testtypen',
            entity: 'xx.XxGenericTestType',
            widgetData: new WidgetData(),
            postFunction: (widget: WidgetConfigInterface) => {
                widget.widgetData.uiComponent = 'selectable';
                widget.widgetData.setParam(WidgetDataParam.create('attributeToDisplay', 'e.name'));
                widget.widgetData.setParam(WidgetDataParam.create('showSearch', true));
            }
        }
    };

    // Parent-child: XxCarPilotCarType → XxCarPilotCarTypeField
    selectedCarPilotCarTypeId: number;
    xxCarPilotCarTypeFieldWidget: WidgetData;

    // Parent-child: XxGenericTestType → XxGenericTestTypeField
    selectedGenericTestTypeId: number;
    xxGenericTestTypeFieldWidget: WidgetData;

    constructor(
        protected route: ActivatedRoute,
        protected coreService: MvsCoreService,
        protected navigationService: MvsObjectNavigationService,
        protected router: Router,
    ) {
        super(route, coreService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    override postNavigationSelection(): void {
        // Lazy-init guard for simple widget
        if (this.activeNavigationItem?.action === 'xx.XxSimpleTestEntity') {
            // Handled via createFunction in widgets map; no action needed here
        }

        // Parent-child: Car-Pilot
        if (this.activeNavigationItem?.action === 'xx.XxCarPilotCarType') {
            const carTypeId = this.route.snapshot.queryParams['carTypeId'];
            if (carTypeId) {
                this.selectedCarPilotCarTypeId = +carTypeId;
                this.refreshCarPilotCarTypeFieldWidget(+carTypeId);
            } else {
                this.selectedCarPilotCarTypeId = undefined;
                if (!this.xxCarPilotCarTypeFieldWidget) {
                    this.refreshCarPilotCarTypeFieldWidgetAll();
                }
            }
        } else {
            // CONFIG-020 rule 3: exit-branch cleanup for Car-Pilot parent-child flow
            if (this.selectedCarPilotCarTypeId !== undefined || this.xxCarPilotCarTypeFieldWidget) {
                this.selectedCarPilotCarTypeId = undefined;
                this.xxCarPilotCarTypeFieldWidget = null;
                if (this.route.snapshot.queryParams['carTypeId']) {
                    this.router.navigate([], {
                        queryParams: {carTypeId: null},
                        queryParamsHandling: 'merge',
                        replaceUrl: true,
                    });
                }
            }
        }

        // Parent-child: Generic Test
        if (this.activeNavigationItem?.action === 'xx.XxGenericTestType') {
            const testTypeId = this.route.snapshot.queryParams['testTypeId'];
            if (testTypeId) {
                this.selectedGenericTestTypeId = +testTypeId;
                this.refreshGenericTestTypeFieldWidget(+testTypeId);
            } else {
                this.selectedGenericTestTypeId = undefined;
                if (!this.xxGenericTestTypeFieldWidget) {
                    this.refreshGenericTestTypeFieldWidgetAll();
                }
            }
        } else {
            // CONFIG-020 rule 3: exit-branch cleanup for Generic Test parent-child flow
            if (this.selectedGenericTestTypeId !== undefined || this.xxGenericTestTypeFieldWidget) {
                this.selectedGenericTestTypeId = undefined;
                this.xxGenericTestTypeFieldWidget = null;
                if (this.route.snapshot.queryParams['testTypeId']) {
                    this.router.navigate([], {
                        queryParams: {testTypeId: null},
                        queryParamsHandling: 'merge',
                        replaceUrl: true,
                    });
                }
            }
        }
    }

    // ── Car-Pilot parent-child ────────────────────────────────────────────────

    onCarPilotCarTypeSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const id = (event as ObjectIdentifierData).data?.['e.id'] ?? event.objectId;
        this.selectedCarPilotCarTypeId = id;
        this.refreshCarPilotCarTypeFieldWidget(id);
        this.router.navigate([], {
            queryParams: {carTypeId: id},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    onCarPilotCarTypeDeselect(): void {
        this.selectedCarPilotCarTypeId = undefined;
        this.refreshCarPilotCarTypeFieldWidgetAll();
        this.router.navigate([], {
            queryParams: {carTypeId: null},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    private refreshCarPilotCarTypeFieldWidgetAll(): void {
        this.xxCarPilotCarTypeFieldWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'xx.car.pilot.car.type.field.widget.all',
            'Alle Car-Pilot Felder',
            'xx.XxCarPilotCarTypeField',
            []
        );
    }

    private refreshCarPilotCarTypeFieldWidget(carTypeId: number): void {
        this.xxCarPilotCarTypeFieldWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'xx.car.pilot.car.type.field.widget.filtered',
            'Zugewiesene Felder',
            'xx.XxCarPilotCarTypeField',
            [FilterCriteria.create('e.carTypeId', FilterCriteria.cOperatorEqual, carTypeId)]
        );
        const fields: WidgetToolbarCreateInterface[] = [{fieldName: 'carTypeDtoId', fieldValue: carTypeId}];
        this.xxCarPilotCarTypeFieldWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
    }

    // ── Generic Test parent-child ─────────────────────────────────────────────

    onGenericTestTypeSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const id = (event as ObjectIdentifierData).data?.['e.id'] ?? event.objectId;
        this.selectedGenericTestTypeId = id;
        this.refreshGenericTestTypeFieldWidget(id);
        this.router.navigate([], {
            queryParams: {testTypeId: id},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    onGenericTestTypeDeselect(): void {
        this.selectedGenericTestTypeId = undefined;
        this.refreshGenericTestTypeFieldWidgetAll();
        this.router.navigate([], {
            queryParams: {testTypeId: null},
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    private refreshGenericTestTypeFieldWidgetAll(): void {
        this.xxGenericTestTypeFieldWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'xx.generic.test.type.field.widget.all',
            'Alle Testfelder',
            'xx.XxGenericTestTypeField',
            []
        );
    }

    private refreshGenericTestTypeFieldWidget(testTypeId: number): void {
        this.xxGenericTestTypeFieldWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'xx.generic.test.type.field.widget.filtered',
            'Zugewiesene Testfelder',
            'xx.XxGenericTestTypeField',
            [FilterCriteria.create('e.testTypeId', FilterCriteria.cOperatorEqual, testTypeId)]
        );
        const fields: WidgetToolbarCreateInterface[] = [{fieldName: 'testTypeDtoId', fieldValue: testTypeId}];
        this.xxGenericTestTypeFieldWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);
    }

    // ── Shared ────────────────────────────────────────────────────────────────

    handleEntitySelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        this.navigationService.navigateTo(
            MvsObjectNavigationEntry.createNavigationEntry(
                event.objectType, event.objectId, 'object', '', '', '',
                MvsObjectNavigationActionEnum.edit
            )
        );
    }

    isConfigurationPage(): boolean {
        return true;
    }

    isNavComponent(): NavigationItem[] {
        return this.navigationItems;
    }

    getPageHeaderConfig(): PageHeaderConfig {
        return {
            component: MvsConfigHeaderComponent,
            inputs: {
                header: 'XX Konfiguration',
                categoryTypeMode: 'ticket',
                icon: 'fa-solid fa-cards-blank'
            }
        };
    }
}
